<div class="main-view space-y-2 md:space-y-4">
    <div class="flex flex-wrap items-start gap-2">
        <div class="min-w-0 flex-1 space-y-1">
            <app-page-surface-header titleIcon="cpu-bolt" [compact]="true" [title]="agent?.name || 'Agent details'"
                subtitle="PD agent configuration, client assignment, and lifecycle status." />
            <p *ngIf="agent?.agent_code" class="text-xs text-gray-500 dark:text-gray-400">
                Agent ID: <span class="font-mono">{{ agent!.agent_code }}</span>
            </p>
        </div>
    </div>

    <div *ngIf="loadError"
        class="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
        {{ loadError }}
        <div class="mt-4">
            <a [routerLink]="backToListLink()" class="btn inline-flex rounded-full bg-white px-4 py-2 text-black">
                Back to agents
            </a>
        </div>
    </div>

    <div *ngIf="loading && !loadError"
        class="rounded-3xl border border-gray-200 bg-white/60 p-8 dark:border-[#191e3a] dark:bg-[#0e1726]">
        <p class="text-sm text-gray-500">Loading agent…</p>
    </div>

    <ng-container *ngIf="agent && !loadError">
        <div
            class="rounded-3xl border border-gray-200 bg-white/60 p-6 shadow-sm dark:border-[#191e3a] dark:bg-[#0e1726] md:p-8">
            <div class="flex flex-wrap items-start gap-4">
                <div
                    class="grid h-16 w-16 shrink-0 place-content-center rounded-2xl bg-primary text-xl font-semibold text-white">
                    {{ (agent.name || 'A').charAt(0).toUpperCase() }}
                </div>
                <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                        <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ agent.name }}</h2>
                        <span class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold"
                            [ngClass]="statusBadgeClass(agent.status)">
                            {{ statusDisplayLabel(agent.status) }}
                        </span>
                    </div>
                    <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        Wizard: {{ wizardStepLabel() }} (step {{ agent.wizard_step }})
                    </p>
                </div>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Product</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{ agent.product?.name || '—' }}
                    </p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Client</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        @if (clientDetailLink(); as clientLink) {
                        <a [routerLink]="clientLink" class="text-primary hover:underline">
                            {{ agent.tenant?.tenant_name || '—' }}
                        </a>
                        } @else {
                        {{ agent.tenant?.tenant_name || '—' }}
                        }
                    </p>
                    <p *ngIf="agent.tenant?.tenant_subdomain" class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {{ agent.tenant!.tenant_subdomain }}
                    </p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Customer profile</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{ agent.customer_type || '—' }}
                        <span *ngIf="agent.customer_sub_profile"> / {{ agent.customer_sub_profile }}</span>
                    </p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Trained on</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{ agent.trained_at | appDate }}
                    </p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Deployed</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{ agent.deployed_at | appDate }}
                    </p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Created</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{ agent.created_at ? (agent.created_at | appDate) : '—' }}
                    </p>
                </div>
            </div>
        </div>

        <!-- PD Sessions -->
        <app-page-body-panel>
            <ng-container ngProjectAs="[pageToolbar]">
                <div class="flex flex-wrap items-center gap-3 w-full">
                    <h3 class="text-base font-bold text-gray-900 dark:text-white flex-1">
                        PD Sessions
                        <span class="ml-2 text-sm font-normal text-gray-400">({{ sessionsTotal }})</span>
                    </h3>
                    <select class="form-select text-xs rounded-xl border border-gray-200 dark:border-white/10 w-32"
                        [(ngModel)]="sessionsStatusFilter" (ngModelChange)="onSessionsFilterChange()">
                        <option value="">All statuses</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="FAILED">Failed</option>
                        <option value="ABANDONED">Abandoned</option>
                    </select>
                    <select class="form-select text-xs rounded-xl border border-gray-200 dark:border-white/10 w-28"
                        [(ngModel)]="sessionsResultFilter" (ngModelChange)="onSessionsFilterChange()">
                        <option value="">All results</option>
                        <option value="PASS">Pass</option>
                        <option value="REVIEW">Review</option>
                        <option value="FAIL">Fail</option>
                    </select>
                </div>
            </ng-container>

            <div *ngIf="sessionsLoading" class="py-8 text-center text-sm text-gray-400">Loading sessions…</div>

            <div *ngIf="!sessionsLoading && sessions.length === 0" class="py-8 text-center text-sm text-gray-400">
                No PD sessions found for this agent.
            </div>

            <div *ngIf="!sessionsLoading && sessions.length > 0" class="overflow-x-auto">
                <table class="w-full text-sm">
                    <thead>
                        <tr
                            class="border-b border-gray-100 dark:border-white/10 text-xs text-gray-500 dark:text-gray-400">
                            <th class="pb-2 text-left font-medium">Session Code</th>
                            <th class="pb-2 text-left font-medium">Customer</th>
                            <th class="pb-2 text-left font-medium">Type</th>
                            <th class="pb-2 text-center font-medium">Status</th>
                            <th class="pb-2 text-center font-medium">Result</th>
                            <th class="pb-2 text-right font-medium">Score</th>
                            <th class="pb-2 text-right font-medium">Date</th>
                            <th class="pb-2 text-right font-medium">Action</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-50 dark:divide-white/5">
                        <tr *ngFor="let s of sessions" class="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                            <td class="py-2.5 font-mono text-xs text-gray-600 dark:text-gray-300">
                                {{ sessionListDisplayCode(s) }}
                            </td>
                            <td class="py-2.5">
                                <p class="font-medium text-gray-900 dark:text-white">{{ s.customer_name || '—' }}</p>
                                <p *ngIf="s.customer_phone" class="text-xs text-gray-400">{{ s.customer_phone }}</p>
                            </td>
                            <td class="py-2.5 text-gray-600 dark:text-gray-300">{{ s.loan_type || '—' }}</td>
                            <td class="py-2.5 text-center">
                                <span [class]="sessionStatusClass(s.status)">{{ s.status }}</span>
                            </td>
                            <td class="py-2.5 text-center" [class]="sessionResultClass(s.result)">
                                {{ s.result || '—' }}
                            </td>
                            <td class="py-2.5 text-right font-mono text-xs">
                                {{ s.score != null ? s.score : '—' }}
                            </td>
                            <td class="py-2.5 text-right text-xs whitespace-nowrap">
                                {{ s.created_at | appDate }}
                            </td>
                            <td class="py-2.5 text-right">
                                @if (canOpenSessionInAuditTrail(s)) {
                                <div class="inline-flex items-center justify-end gap-1">
                                    <button
                                        type="button"
                                        class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
                                        title="View"
                                        aria-label="View"
                                        (click)="viewSession(s, $event)"
                                    >
                                        <icon-eye class="h-4 w-4" />
                                    </button>
                                </div>
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Pagination -->
                <div *ngIf="sessionsTotalPages() > 1"
                    class="mt-4 flex items-center justify-between text-xs text-gray-500">
                    <span>Page {{ sessionsPage }} of {{ sessionsTotalPages() }}</span>
                    <div class="flex gap-2">
                        <button class="btn rounded-full border border-gray-200 px-3 py-1 disabled:opacity-40"
                            [disabled]="sessionsPage <= 1" (click)="onSessionsPageChange(sessionsPage - 1)">
                            Prev
                        </button>
                        <button class="btn rounded-full border border-gray-200 px-3 py-1 disabled:opacity-40"
                            [disabled]="sessionsPage >= sessionsTotalPages()"
                            (click)="onSessionsPageChange(sessionsPage + 1)">
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </app-page-body-panel>

        <app-page-body-panel>
            <ng-container ngProjectAs="[pageToolbar]">
                <h3 class="text-base font-bold text-gray-900 dark:text-white">Actions</h3>
            </ng-container>
            <div class="flex flex-wrap gap-3">
                <ng-container *ngIf="retrainLink() as retrainNav">
                    <a [routerLink]="retrainNav.commands" [queryParams]="retrainNav.queryParams"
                        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white">
                        <icon-server class="size-4" />
                        Open training
                    </a>
                </ng-container>
                <button *ngIf="canToggleStatus()" type="button"
                    class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium disabled:opacity-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white"
                    [disabled]="toggleLoading" (click)="onToggleStatus()">
                    <icon-play-circle class="size-4" />
                    {{ toggleLoading ? 'Updating…' : toggleStatusLabel() }}
                </button>
            </div>
        </app-page-body-panel>
    </ng-container>
</div>